import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import ImageUploadModal from '../components/ImageUploadModal'
import StickerMockup from '../components/StickerMockup'
import './Shop.css'

function Shop() {
  const { token, user } = useAuth()
  const navigate = useNavigate()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState([])
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [selectedProductForUpload, setSelectedProductForUpload] = useState(null)
  const [customImages, setCustomImages] = useState({}) // productId -> image data

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : {}
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/products?populate=*`, { headers })
        if (!response.ok) {
          const errorData = await response.json()
          console.error('❌ API Error Response:', JSON.stringify(errorData, null, 2))
          if (errorData.error) {
            console.error('❌ Error Details:', errorData.error.message || errorData.error)
          }
          throw new Error(`API Error: ${response.status}`)
        }
        const data = await response.json()
        console.log('✅ Products fetched:', data.data?.length || 0, 'products')
        const products = (data.data || []).map(item => ({
          id: item.id,
          ...item.attributes
        }))
        setProducts(products)
      } catch (error) {
        console.error('❌ Error fetching products:', error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [token])

  const getDescriptionText = (desc) => {
    if (typeof desc === 'string') return desc
    if (Array.isArray(desc)) {
      return desc
        .map(block => block.children?.map(child => child.text).join('') || '')
        .join(' ')
    }
    return ''
  }

  const getImageUrl = (image) => {
    if (!image) return null
    if (Array.isArray(image.data)) {
      return image.data[0]?.attributes?.url
    }
    return image.data?.attributes?.url || image.url
  }

  const openUploadModal = (product) => {
    setSelectedProductForUpload(product)
    setUploadModalOpen(true)
  }

  const handleImageSelect = (imageData) => {
    if (selectedProductForUpload) {
      // Store custom image for this product
      setCustomImages({
        ...customImages,
        [selectedProductForUpload.id]: imageData
      })

      // Add product to cart with custom image (preview + base64 data)
      const cartItem = {
        ...selectedProductForUpload,
        customImageData: imageData.data, // Base64 data URL (will be saved at checkout)
        customImageFilename: imageData.filename,
        customImagePreview: imageData.preview
      }
      setCart([...cart, cartItem])
      alert(`${selectedProductForUpload.name} met aangepast ontwerp toegevoegd aan winkelwagen!`)
      setUploadModalOpen(false)
      setSelectedProductForUpload(null)
    }
  }

  const addToCart = (product) => {
    setCart([...cart, product])
    alert(`${product.name} toegevoegd aan winkelwagen!`)
  }

  const handleCheckout = async () => {
    if (cart.length === 0) {
      alert('Winkelwagen is leeg!')
      return
    }

    try {
      const totalPrice = Math.round(cart.reduce((sum, item) => sum + (item.price || 0), 0) * 100)

      // Create order with cart data
      const orderResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          data: {
            items: JSON.stringify(cart.map(item => ({
              id: item.id,
              name: item.name,
              price: item.price
            }))),
            orderTotal: totalPrice,
            status: 'pending',
            customer_name: user?.username || 'Guest',
            customer_email: user?.email || ''
          }
        })
      })

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json()
        console.error('❌ Order creation error:', JSON.stringify(errorData, null, 2))
        if (errorData.error) {
          console.error('❌ Error message:', errorData.error.message || errorData.error)
        }
        throw new Error('Kan bestelling niet aanmaken')
      }

      const orderData = await orderResponse.json()
      console.log('✅ Order created:', orderData.data.id)

      // Clear cart and redirect to checkout
      setCart([])
      navigate(`/checkout/${orderData.data.id}`)
    } catch (error) {
      console.error('Checkout error:', error)
      alert('Kan bestelling niet verwerken. Probeer het later opnieuw.')
    }
  }

  return (
    <div className="shop">
      <ImageUploadModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false)
          setSelectedProductForUpload(null)
        }}
        onImageSelect={handleImageSelect}
        productName={selectedProductForUpload?.name}
      />

      <h1>Winkel Stickers & Ontwerpen</h1>
      <p className="subtitle">Blader en koop aangepaste stickers en ontwerpen</p>

      {loading ? (
        <div className="loading">Producten worden geladen...</div>
      ) : products.length === 0 ? (
        <div className="no-products">
          <p>Nog geen producten beschikbaar. Kom binnenkort terug!</p>
        </div>
      ) : (
        <>
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-card">
                {product.image && getImageUrl(product.image) && (
                  <img src={`${import.meta.env.VITE_API_URL}${getImageUrl(product.image)}`} alt={product.name} className="product-image" />
                )}
                <div className="product-details">
                  <h3>{product.name}</h3>
                  <p className="product-description">{getDescriptionText(product.description)}</p>

                  {/* Show custom sticker mockup if image uploaded */}
                  {customImages[product.id] && (
                    <StickerMockup
                      imageUrl={customImages[product.id].preview}
                      isCustom={true}
                    />
                  )}

                  <p className="product-price">€{product.price ? product.price.toFixed(2) : '0.00'}</p>

                  <div className="product-actions">
                    <button
                      className="btn-upload-design"
                      onClick={() => openUploadModal(product)}
                    >
                      📤 Aangepast Ontwerp Uploaden
                    </button>
                    <button
                      className="btn-add-to-cart"
                      onClick={() => addToCart(product)}
                    >
                      Toevoegen aan Winkelwagen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {cart.length > 0 && (
            <div className="cart-summary">
              <h3>Winkelwagen ({cart.length} items)</h3>
              <p>Totaal: €{cart.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)}</p>
              <button className="btn-checkout" onClick={handleCheckout}>
                Doorgaan naar Afrekenen
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Shop
